import type { Messages } from "@/i18n/messages/sq"

export const en: Messages = {
    meta: {
        lang: "en",
        dir: "ltr",
        siteTagline: "Publishing, translation and courses",
        siteDescription:
            "Germa Institute is a cultural and educational institution dedicated to publishing, translation, Quran courses and foreign language teaching."
    },

    languageNames: {
        sq: "Albanian",
        en: "English",
        de: "German",
        es: "Spanish",
        tr: "Turkish",
        bs: "Bosnian",
        ar: "Arabic"
    },

    common: {
        home: "Home",
        homeAria: "Germa Institute, home page",
        breadcrumb: "Breadcrumb",
        mainNav: "Main",
        menu: "Menu",
        openMenu: "Open menu",
        closeMenu: "Close menu",
        contactUs: "Contact us",
        language: "Language",
        chooseLanguage: "Choose the site language",
        instituteName: "Germa Institute"
    },

    nav: {
        libra: "Books",
        kurani: "Quran",
        videoleksione: "Video lessons",
        kurse: "Courses",
        audiolibra: "Audiobooks",
        rrethNesh: "About us",
        kontakt: "Contact"
    },

    footer: {
        tagline:
            "Publishing and translating Islamic literature, with courses in Quran reading and foreign languages.",
        links: "Quick links",
        books: "Books",
        allBooks: "All books",
        contact: "Contact",
        contactNote:
            "Write to us to order books, sign up for a course or propose a collaboration.",
        rights: "All rights reserved."
    },

    verse: {
        text: "Say: “My Lord, increase me in knowledge.”",
        source: "Quran, 20:114"
    },

    home: {
        seoDescription:
            "Free books to download, video lessons, audiobooks and courses in Quran reading and foreign languages, from Germa Institute.",
        hero: {
            eyebrow: "Welcome",
            intro:
                "Germa Institute is a cultural and educational institution. We publish religious, linguistic and educational books, produce video lessons and run courses in Quran reading and foreign languages.",
            primaryCta: "Browse our books",
            secondaryCta: "Join a course",
            pillars: ["Books", "Video lessons", "Courses", "Audiobooks"],
            cue: "Discover more",
            category: "Publication",
            viewBook: "View the book",
            previous: "Previous book",
            next: "Next book",
            goTo: "Go to book {number}"
        },
        marquee: {
            rowOne: ["Publishing", "Translation", "Quran courses", "Video lessons", "Audiobooks", "Foreign languages"],
            rowTwo: ["The Qur’anic Primer", "Tajweed", "Knowledge", "Education", "Heritage", "A modern method"]
        },
        about: {
            eyebrow: "About the institute",
            title: "In the service of knowledge and education",
            intro:
                "Our work is about helping people learn, deepen their understanding of their faith and build their language skills. That is why we make sure our books and learning materials are accurate and clearly explained, and that our courses meet each student where they are.",
            features: [
                "Publishing and translating religious, linguistic and educational literature.",
                "Courses in Quran reading and foreign languages, online and in the classroom.",
                "Video lessons and audiobooks you can follow online."
            ]
        },
        stats: {
            titles: "Titles published",
            languages: "Publication languages",
            fields: "Areas of work",
            online: "Online access"
        },
        services: {
            eyebrow: "What we offer",
            title: "Our work",
            more: "Learn more",
            items: {
                libra: {
                    title: "Books",
                    description:
                        "Religious, linguistic and educational books, written or translated by Germa Institute.",
                    cta: "See the books"
                },
                videoleksione: {
                    title: "Video lessons",
                    description: "Video lessons on reading the Quran and other religious and educational topics.",
                    cta: "Watch the lessons"
                },
                kurse: {
                    title: "Courses",
                    description:
                        "Courses in Quran reading and foreign languages, in the classroom and online.",
                    cta: "See the courses"
                },
                audiolibra: {
                    title: "Audiobooks",
                    description: "Our publications read aloud, for those who prefer to listen.",
                    cta: "Listen to the audiobooks"
                }
            }
        },
        booksLocal: {
            title: "Books in English",
            description: "Our English editions, all free to download."
        },
        booksOther: {
            title: "Books in other languages",
            description: "Our publications in Albanian and other languages, also free to download."
        },
        booksCarouselTitle: "Books that leave a mark",
        booksCarouselDescription:
            "Religious, educational and cultural works, carefully prepared for the reader.",
        seeAllBooks: "See all books",
        mostRead: {
            eyebrow: "Reader favourites",
            title: "Most read",
            description: "The books our readers open and download most often."
        },
        kurani: {
            badge: "A special project",
            title: "The Noble Quran",
            subtitle: "A translation of its meanings into Albanian",
            text: "Read the Quran alongside the translation of its meanings into Albanian by Jusuf H. Kastrati, available free of charge on Kurani.info.",
            cta: "About the translation",
            readOn: "Read on Kurani.info",
            logoAlt: "Kurani.info logo",
            highlights: [
                {
                    title: "Accuracy and faithfulness",
                    text: "The meanings of the verses are conveyed with care, drawing on trusted works of tafsir."
                },
                {
                    title: "Clear, flowing Albanian",
                    text: "Natural Albanian that, as far as the language allows, preserves the distinctive style of Quranic expression."
                },
                {
                    title: "Explanatory footnotes",
                    text: "Short notes that help the reader understand a verse wherever further explanation is needed."
                }
            ]
        }
    },

    cta: {
        eyebrow: "Get involved",
        title: "To join a course, order our publications or work with us, get in touch.",
        primary: "See the courses",
        secondary: "Contact us"
    },

    books: {
        seoTitle: "Islamic books",
        seoDescription:
            "The Germa Institute catalogue: religious, educational and cultural books in Albanian, English, German and other languages, professionally translated and edited.",
        listName: "Publications of Germa Institute",
        title: "Our publications",
        description:
            "Here you will find our publications in English, Albanian and other languages, all free to download.",
        crumb: "Books",
        searchLabel: "Search books",
        searchPlaceholder: "Search by title or author",
        authorChip: "Author: {name}",
        clearAuthor: "Remove the author filter",
        clearSearch: "Clear search",
        categoryLabel: "Category",
        allCategories: "All categories",
        languageLabel: "Language",
        allLanguages: "All languages",
        readingList: "My reading list",
        readingListHintBefore: "Tap the",
        readingListHintAfter: "icon on any book to save it here.",
        readingListEmpty:
            "Your reading list is empty for now. Tap the bookmark on any book you would like to read later.",
        foundOne: "1 book found",
        foundMany: "{count} books found",
        noResults: "No books match these filters.",
        showAll: "Show all books",
        groupLocal: "In English",
        groupOther: "In other languages",
        mostRead: "Most read",
        publishedBy: "Published by Germa Institute",
        viewBook: "View the book {title}",
        coverAlt: "Cover of {title}",
        save: "Save to my list",
        saved: "In my list",
        remove: "Remove from my list"
    },

    bookPage: {
        lang: "en",
        dir: "ltr",
        about: "About the book",
        highlights: "Key points",
        details: "Details",
        language: "Language",
        languageName: "English",
        category: "Category",
        author: "Author",
        authorLine: "Author: {author}.",
        publishedBy: "Published by Germa Institute.",
        fallbackDescription: "{title}. A book in {language}.",
        publisher: "Publisher",
        cta: "Interested in this book? Write to us to order it or to find out more.",
        contact: "Contact us",
        home: "Home",
        books: "Books",
        book: "Book",
        breadcrumb: "Breadcrumb",
        coverAlt: "Cover of {title}",
        readOnline: "Read online",
        orderOrAsk: "Order or ask",
        allBooks: "All books",
        readerTitle: "Read the book online",
        readerText: "Browse the whole book right here, free of charge.",
        openNewTab: "Open in a new tab",
        relatedEyebrow: "Keep reading",
        moreByAuthor: "More books by this author",
        allByAuthor: "All books by this author",
        authorLinkTitle: "See this author’s books",
        relatedTitle: "You may also like",
        seeAllBooks: "See all books",
        otherEditions: "This book in other languages",
        watchVideos: "Watch the video lessons",
        downloadPdf: "Download PDF",
        continueFrom: "Continue from page {page}",
        startOver: "Start from the beginning",
        page: "Page",
        pageOf: "of",
        prevPage: "Previous page",
        nextPage: "Next page",
        zoomIn: "Zoom in",
        zoomOut: "Zoom out",
        fullscreen: "Full screen",
        closeReader: "Close reader",
        loadingBook: "Opening the book…",
        readerError: "The book could not be opened here. Try opening it in a new tab.",
        saveBook: "Save to my list",
        savedBook: "In my list",
        share: "Share",
        copyLink: "Copy link",
        linkCopied: "Link copied",
        email: "Email",
        moreOptions: "More options",
        displaySettings: "Reading view",
        themeLight: "Day",
        themeSepia: "Sepia",
        themeNight: "Night",
        pageSize: "Text size",
        reviews: {
            title: "Reader reviews",
            intro: "What did you think of this book? Leave a star rating and, if you like, a few words for other readers.",
            average: "{average} out of 5",
            count: "{count} reviews",
            countOne: "1 review",
            empty: "No reviews yet. Be the first to rate this book.",
            formTitle: "Rate this book",
            ratingLabel: "Your rating",
            star: "{count} stars",
            starOne: "1 star",
            name: "Your name",
            namePlaceholder: "e.g. Sarah",
            comment: "Comment (optional)",
            commentPlaceholder: "What did you like about this book?",
            submit: "Send review",
            sending: "Sending…",
            success: "Thank you! Your review will appear once the Institute has checked it.",
            errorRating: "Please choose how many stars you give the book.",
            errorName: "Please enter your name (at least two letters).",
            errorThrottled: "You have sent many reviews today. Please try again tomorrow.",
            error: "The review could not be sent. Please try again in a moment.",
            moderated: "Reviews appear after the Institute has checked them."
        },
        quote: {
            button: "Share a quote",
            title: "Share a quote",
            intro: "Select a sentence on the book page with your mouse or finger, or type it here. We turn it into an image for Instagram or WhatsApp.",
            textLabel: "Quote",
            placeholder: "Type or paste the sentence that moved you…",
            formatLabel: "Image shape",
            square: "Square (post)",
            story: "Tall (story)",
            share: "Share the image",
            download: "Download the image",
            copyText: "Copy the text",
            copied: "Text copied",
            close: "Close",
            tooLong: "The quote is too long. Please shorten it (up to {max} characters).",
            empty: "Write a quote to show it on the image.",
            fromSelection: "Share as a quote",
            preview: "Preview of the image",
            shareText: "From the book “{title}”"
        }
    },

    share: {
        share: "Share",
        copyLink: "Copy link",
        linkCopied: "Link copied",
        email: "Email",
        moreOptions: "More options"
    },

    audiobooks: {
        seoTitle: "Islamic audiobooks in Albanian",
        seoDescription:
            "Religious and educational audiobooks in Albanian, read clearly and professionally. Listen right on the page, anytime and on any device.",
        listName: "Audiobooks from Germa Institute",
        title: "Audiobooks in Albanian",
        description:
            "Our publications in audio format, in Albanian. Listen right here on the page or download them free of charge, no sign-up needed.",
        crumb: "Audiobooks",
        comingSoon: "Coming soon",
        comingSoonText: "Our audiobooks are being prepared. Follow our channels to be the first to hear them.",
        badge: "Audiobook",
        author: "Author:",
        openBook: "Open the book",
        resumeFrom: "Resume at {time}",
        back15: "Back 15 seconds",
        forward15: "Forward 15 seconds",
        play: "Play",
        pause: "Pause",
        position: "Playback position",
        speedGroup: "Playback speed",
        speed: "Speed",
        speedValue: "Speed {value}",
        download: "Download the audiobook",
        youtube: "Listen on YouTube",
        miniLabel: "Now playing",
        closePlayer: "Close the player",
        openAudiobook: "Go to the audiobook",
        coverAlt: "Cover of the audiobook {title}",
        otherAudiobooks: "More audiobooks",
        allAudiobooks: "All audiobooks",
        openPage: "Open the audiobook page",
        pageSeoDescription: "Listen to the audiobook “{title}” in Albanian for free, right on the page, or download it without signing up."
    },

    videos: {
        seoTitle: "Video lessons for learning to read the Quran",
        seoDescription:
            "{count} free video lessons for learning the letters of the Quran, in Albanian, German and English, from the very first letter to fluent reading.",
        listName: "Video lessons from Germa Institute",
        title: "Learn to read the Quran",
        description:
            "The Qur’anic Primer video lessons take you step by step through the Arabic letters and on to reading the Quran. The lessons are in Albanian, German and English, and you can follow them here free of charge, with no sign-up.",
        crumb: "Video lessons",
        chooseLanguage: "Choose a language:",
        player: {
            videos: "videos",
            youtube: "Watch on YouTube",
            list: "Lesson list",
            promo: "Introductory video",
            promoShort: "Intro",
            lesson: "Lesson",
            of: "of",
            playing: "Now playing",
            shareLesson: "Share this lesson",
            openBook: "View the book",
            markDone: "Mark as learned",
            done: "Learned",
            progress: "learned",
            resume: "Continue with",
            lessonsDone: "{done} of {total} lessons learned",
            progressLabel: "Progress in this series",
            seriesDone: "You have completed this series. Well done!",
            progressHint: "Mark the lessons you have learned and follow your progress.",
            prevLesson: "Previous lesson",
            nextLesson: "Next lesson",
            allLessons: "All lessons in this series",
            watchOnYoutube: "Watch on YouTube",
            aboutLesson: "About this lesson",
            backToSeries: "Back to the series",
            openLessonPage: "Open the lesson page",
            lessonIntro: "Lesson {number} of {total} in the series “{series}”.",
            promoIntro: "Introductory video of the series “{series}”.",
            freeLesson: "A free video lesson from the Germa Institute, no sign-up needed."
        }
    },

    courses: {
        seoTitle: "Quran and language courses",
        seoDescription:
            "Quran courses from the very first letters to reading with tajweed, plus language courses. Online and in the classroom, with registration open.",
        listName: "Courses at Germa Institute",
        title: "Courses in Quran reading and languages",
        description:
            "We run courses for beginners and for those who want to build on what they already know. Depending on the course and the group, lessons take place online or in the classroom.",
        crumb: "Courses",
        posterAlt: "Poster for the course {title}",
        teacher: "Teacher:",
        details: "See the course details",
        listTitle: "Our courses",
        listDescription:
            "We teach Quran reading and tajweed, and we are planning to bring back our Arabic language course. Dates, times and how to register are announced for each new group.",
        registerTitle: "Would you like to join a course?",
        registerDescription:
            "Fill in the form to let us know you are interested. As soon as a new group opens for the course you have chosen, we will contact you with the start date, the timetable and how the lessons will run.",
        plannedSuffix: "planned to resume",
        contactFallback: "Contact us",
        items: {
            "shkronjat-e-kuranit": {
                title: "Learning to read the Quran",
                description:
                    "A course for women who are complete beginners, based on the book The Qur’anic Primer. It starts with recognising and pronouncing the Arabic letters, then moves on to joining them into words and practising Quran reading.",
                level: "For women, beginners",
                format: "Online",
                badge: "For beginners",
                summary:
                    "Based on The Qur’anic Primer, the course starts with recognising and pronouncing the Arabic letters, then moves on to joining them into words and reading practice.",
                cta: "Ask us about new groups"
            },
            "leximi-me-texhvid": {
                title: "Learning tajweed",
                description:
                    "Learning and applying the rules of tajweed, with practical exercises and individual correction of your recitation.",
                level: "For those who can already read the Quran",
                format: "Online",
                badge: "",
                summary: "",
                cta: "Ask us about new groups"
            },
            "gjuha-arabe": {
                title: "Arabic language",
                description:
                    "A course in reading, writing and understanding Arabic. Details will be announced once a restart date has been set.",
                level: "Planned to resume",
                format: "Online",
                badge: "",
                summary: "",
                cta: "Contact us for more information"
            }
        }
    },

    courseForm: {
        name: "Full name",
        phone: "Phone number",
        email: "Email address",
        course: "Course you are interested in",
        message: "Message",
        optional: "(optional)",
        messagePlaceholder: "Add any questions or requests you have.",
        notice: "Filling in this form does not count as final registration for the course.",
        submit: "Send request",
        submitting: "Sending…",
        successTitle: "Request sent",
        successText:
            "Thank you for your interest. We will contact you as soon as a new group opens for the course you have chosen.",
        again: "Send another request",
        errors: {
            name: "Please enter your name.",
            phone: "Please enter a valid phone number.",
            email: "Please enter a valid email address.",
            course: "Please choose a course.",
            generic: "Your request could not be sent. Please try again."
        }
    },

    contact: {
        seoTitle: "Contact",
        seoDescription:
            "Write to Germa Institute to order books, register for Quran and language courses or discuss a collaboration. We reply as quickly as we can.",
        title: "Contact us",
        description:
            "Write to us to order books, register for a course, propose a collaboration or ask about our work.",
        crumb: "Contact",
        howTitle: "How to reach us",
        howText: "You can write to us by email or use the form.",
        email: "Email",
        response: "Response time",
        responseValue: "Within 3 days",
        follow: "Follow us on social media"
    },

    contactForm: {
        name: "Full name",
        namePlaceholder: "Your full name",
        email: "Email address",
        emailPlaceholder: "you@example.com",
        subject: "Subject",
        optional: "(optional)",
        subjectPlaceholder: "What would you like to write about?",
        message: "Message",
        messagePlaceholder: "Write your message here.",
        submit: "Send message",
        submitting: "Sending…",
        successTitle: "Thank you!",
        successText: "Your message has been sent. We will get back to you as soon as possible.",
        again: "Send another message",
        errors: {
            name: "Please enter your name.",
            email: "Please enter a valid email address.",
            message: "Please write a slightly longer message.",
            generic: "Your message could not be sent. Please try again."
        }
    },

    about: {
        seoTitle: "About us",
        seoDescription:
            "Germa Institute is a cultural and educational institution founded by Jusuf Kastrati, devoted to publishing, translation and Quran and language courses.",
        title: "Germa Institute",
        description: "In the service of knowledge and education, through books and teaching.",
        crumb: "About us",
        missionTitle: "Our mission",
        mission: [
            "Germa Institute is a cultural and educational institution working in publishing, translation and teaching. We publish religious, linguistic and educational literature, run courses in Quran reading and languages, and produce video lessons and learning materials.",
            "In everything we publish, we pay close attention to accurate content, careful translation and thorough language editing. In our courses, we combine explanation with practical exercises, adapted to each student’s level and needs."
        ],
        valuesTitle: "The principles behind our work",
        values: [
            {
                title: "Accuracy",
                text: "We rely on trusted sources and review every text before publication, with care for both religious and linguistic accuracy."
            },
            {
                title: "Clarity",
                text: "We write in fluent, accessible language and explain the terms and topics that call for some background knowledge."
            },
            {
                title: "Care for the learner",
                text: "We adapt our lessons to each student’s age and knowledge, make room for questions and help everyone progress step by step."
            },
            {
                title: "Responsibility",
                text: "We welcome comments and suggestions, and we correct any mistakes found in our publications and materials."
            }
        ],
        leadership: "Leadership",
        founderShort: "Founder & director",
        portraitAlt: "Portrait of {name}",
        directorRole: "Founder and director of Germa Institute",
        directorBio: [
            "Germa Institute is led by its founder and director, Jusuf Kastrati. He graduated from the Faculty of Sharia at Imam Muhammad ibn Saud Islamic University in Riyadh, specialising in Islamic jurisprudence and its principles. He went on to complete a master’s degree in Islamic Culture at the same university, and is currently pursuing a doctorate in Islamic Civilisation at Ez-Zitouna University in Tunisia.",
            "He has many years of experience in translating Islamic literature and in teaching. He has translated religious books and taught courses in Quran reading and Arabic, with explanations in Albanian and German, and has devoted a special part of his work to the religious education of young people."
        ]
    },

    kurani: {
        seoTitle: "The Noble Quran with an Albanian translation of its meanings",
        seoDescription:
            "Read the Arabic text of the Quran alongside the translation of its meanings into Albanian by Jusuf Kastrati, free of charge on Kurani.info.",
        title: "The Noble Quran",
        subtitle: "A translation of its meanings into Albanian",
        description:
            "Read the Quran alongside the translation of its meanings into Albanian by Jusuf H. Kastrati. The translation is available free of charge on Kurani.info.",
        crumb: "Quran",
        readOn: "Read on Kurani.info",
        logoAlt: "Kurani.info logo",
        aboutTitle: "About the translation",
        about: [
            "This translation was prepared to convey the meanings of the verses to Albanian readers accurately, in clear and flowing Albanian. It draws on trusted works of tafsir and has been through many rounds of religious and linguistic review.",
            "Every translation is a human attempt to convey the meanings of the verses, and none can fully capture the linguistic richness and depth of the original. It is therefore an aid to understanding the Quran, while the Quran itself remains the Book revealed by Allah in Arabic."
        ],
        featuresTitle: "What sets the translation apart",
        features: [
            {
                title: "Accuracy and faithfulness",
                text: "Great care in conveying the meanings of the verses, drawing on trusted works of tafsir."
            },
            {
                title: "Clear, flowing Albanian",
                text: "Clear and fluent Albanian that, as far as the language allows, preserves the distinctive style of Quranic expression."
            },
            {
                title: "Consistent style and terminology",
                text: "Terminology is used consistently, and established Islamic terms familiar to Albanian readers are retained."
            },
            {
                title: "Brief footnotes",
                text: "Short notes that help the reader understand a verse wherever extra explanation is needed."
            },
            {
                title: "Religious and linguistic review",
                text: "Repeated reviews by specialists in the relevant fields, for accuracy of meaning and of expression in Albanian."
            },
            {
                title: "Free access",
                text: "The translation can be read online free of charge, and the digital edition allows it to be updated with further corrections and improvements."
            }
        ],
        translatorBadge: "Translator",
        translatorRole: "Doctoral candidate in Islamic Civilisation",
        translatorText:
            "The translation of the meanings of the Noble Quran into Albanian is the fruit of many years of research, translation and editing.",
        teamTitle: "Editing and review team",
        roles: {
            religious: "Religious editing",
            consulting: "Religious advisor",
            language: "Language editing"
        },
        noteTitle: "Please note",
        note: "This translation is in the final stages of editing and scholarly review. We welcome any comments or suggestions that can help convey the meanings of the Noble Quran as accurately as possible.",
        writeUs: "Write to us",
        ctaTitle: "Read the translation",
        ctaText: "You will find the Arabic text, the Albanian translation and the explanatory footnotes on Kurani.info.",
        ctaButton: "Open Kurani.info"
    },

    search: {
        open: "Search",
        title: "Search the portal",
        placeholder: "Search books, audiobooks, video lessons, courses…",
        close: "Close search",
        hint: "Type at least two letters to search.",
        empty: "Nothing found for “{query}”.",
        error: "The search did not work. Please try again in a moment.",
        loading: "Searching…",
        groups: {
            books: "Books",
            audiobooks: "Audiobooks",
            videos: "Video lessons",
            courses: "Courses"
        }
    },

    offline: {
        title: "You are offline",
        text: "This page has not been saved on this device yet. Please try again once you are back online. In the meantime, you can read the books you opened before.",
        retry: "Try again",
        home: "Home page",
        savedTitle: "Books saved for offline reading",
        savedEmpty: "You have no saved books yet. Every book you open in the site’s reader is saved on this device automatically."
    },

    pwa: {
        install: "Install the app"
    },

    notFound: {
        title: "Page not found",
        text: "The page may have moved or no longer exists. Head back to the home page to continue your journey of learning.",
        back: "Back to the home page"
    },

    serverError: {
        title: "Something went wrong",
        text: "An unexpected error occurred on our side. Please try again in a moment.",
        back: "Back to the home page"
    }
}
